import { resolveCmsDevProxyUrl } from './cms-dev-proxy-url';

type CachedAsset = {
  objectUrl: string;
  blob: Blob;
};

export type AssetPrefetchOptions = {
  signal?: AbortSignal;
};

const cachedAssets = new Map<string, CachedAsset>();
const downloadingAssets = new Map<string, Promise<string>>();

export function getObjectUrl(url: string): string | undefined {
  return cachedAssets.get(resolveFetchUrl(url))?.objectUrl;
}

export function isPrefetched(url: string): boolean {
  return cachedAssets.has(resolveFetchUrl(url));
}

export function getCachedAssetURLIfExists(url: string): string {
  return cachedAssets.get(resolveFetchUrl(url))?.objectUrl ?? url;
}

export async function prefetch(url: string, options: AssetPrefetchOptions = {}): Promise<string> {
  const fetchUrl = resolveFetchUrl(url);

  const cached = cachedAssets.get(fetchUrl);
  if (cached) {
    return cached.objectUrl;
  }

  const pending = downloadingAssets.get(fetchUrl);
  if (pending) {
    return pending;
  }

  const download = downloadToObjectUrl(fetchUrl, options.signal);
  downloadingAssets.set(fetchUrl, download);

  try {
    return await download;
  } finally {
    downloadingAssets.delete(fetchUrl);
  }
}

export function revoke(url: string): void {
  const fetchUrl = resolveFetchUrl(url);
  const entry = cachedAssets.get(fetchUrl);
  if (!entry) return;
  URL.revokeObjectURL(entry.objectUrl);
  cachedAssets.delete(fetchUrl);
}

export function revokeAll(): void {
  for (const entry of cachedAssets.values()) {
    URL.revokeObjectURL(entry.objectUrl);
  }
  cachedAssets.clear();
  downloadingAssets.clear();
}

function resolveFetchUrl(url: string): string {
  return resolveCmsDevProxyUrl(url) ?? url;
}

async function downloadToObjectUrl(fetchUrl: string, signal?: AbortSignal): Promise<string> {
  const response = await fetch(fetchUrl, { signal });
  if (!response.ok) {
    throw new Error(`Asset prefetch failed (${response.status}): ${fetchUrl}`);
  }

  const blob = blobForPlayback(await response.blob(), fetchUrl);
  const objectUrl = URL.createObjectURL(blob);
  cachedAssets.set(fetchUrl, { objectUrl, blob });
  return objectUrl;
}

const AUDIO_MIME_BY_EXTENSION: Readonly<Record<string, string>> = {
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  flac: 'audio/flac',
};

function extensionFromUrl(url: string): string | undefined {
  const path = url.split('?')[0]?.split('#')[0] ?? url;
  const lastSegment = path.split('/').pop() ?? '';
  const dotIndex = lastSegment.lastIndexOf('.');
  if (dotIndex < 0) return undefined;
  return lastSegment.slice(dotIndex + 1).toLowerCase();
}

function isPlayableMediaType(mime: string): boolean {
  return mime.startsWith('audio/') || mime.startsWith('video/');
}

function blobForPlayback(blob: Blob, fetchUrl: string): Blob {
  if (isPlayableMediaType(blob.type)) return blob;
  const inferredMime = AUDIO_MIME_BY_EXTENSION[extensionFromUrl(fetchUrl) ?? ''];
  if (!inferredMime) return blob;
  return blob.slice(0, blob.size, inferredMime);
}
